/**
 * FileListItem Component
 *
 * Renders a single file item in the POReviewer file list drawer.
 * Shows extraction status, save status, and provides actions.
 */

const EXTRACTION_STATUS = {
  PENDING: 'pending',
  EXTRACTING: 'extracting',
  DONE: 'done',
  ERROR: 'error'
};

/**
 * Status icon based on extraction/save state
 */
function StatusIcon({ status, isSaved }) {
  if (isSaved) {
    return (
      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
        <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }

  switch (status) {
    case EXTRACTION_STATUS.ERROR:
      return (
        <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    case EXTRACTION_STATUS.EXTRACTING:
      return (
        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      );
    case EXTRACTION_STATUS.DONE:
      return (
        <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
          <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
          </svg>
        </div>
      );
    default:
      return (
        <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
        </div>
      );
  }
}

/**
 * Extraction status badge
 */
function ExtractionBadge({ status, isSaved }) {
  if (status === EXTRACTION_STATUS.DONE || isSaved) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
        Extracted
      </span>
    );
  }
  if (status === EXTRACTION_STATUS.EXTRACTING) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
        Extracting
      </span>
    );
  }
  if (status === EXTRACTION_STATUS.ERROR) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
      Pending
    </span>
  );
}

/**
 * FileListItem Component
 * @param {Object} props
 * @param {Object} props.file - File object with objectKey, originalName
 * @param {number} props.index - Index in the file list
 * @param {string} props.status - Extraction status
 * @param {boolean} props.isSaved - Whether the file has been saved
 * @param {boolean} props.isActive - Whether this is the currently selected file
 * @param {function} props.onClick - Click handler to select this file
 * @param {function} props.onRemove - Handler to remove this file
 */
function FileListItem({ file, index, status, isSaved, isActive, onClick, onRemove }) {
  const displayName = file.originalName || file.objectKey.split('/').pop();

  return (
    <button
      onClick={onClick}
      className={`
        w-full px-4 py-3 text-left border-b border-gray-100 hover:bg-gray-50
        transition-colors flex items-start gap-3
        ${isActive ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}
      `}
    >
      {/* Status icon */}
      <div className="flex-shrink-0 mt-0.5">
        <StatusIcon status={status} isSaved={isSaved} />
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isActive ? 'font-medium text-blue-700' : 'text-gray-700'}`}>
          {index + 1}. {displayName}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <ExtractionBadge status={status} isSaved={isSaved} />
          {isSaved && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
              Saved
            </span>
          )}
        </div>
      </div>

      {/* Delete button (only if not saved) */}
      {!isSaved && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Remove "${displayName}" from the list?`)) {
              onRemove();
            }
          }}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
          title="Remove from list"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}

      {/* Active indicator */}
      {isActive && !isSaved && (
        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}

export default FileListItem;
export { EXTRACTION_STATUS };
